import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    LocationDescription,
    LocationDescriptionUpdated,
    LocationDescriptionOnServerCreated,
    selectLocationDescriptionsActionLoading,
    selectLocationDescriptionById,
    selectLastCreatedLocationDescriptionId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-location-description-edit',
    templateUrl: './location-description-edit.component.html',
    styleUrls: ['./location-description-edit.component.scss'],
})


export class LocationDescriptionEditComponent implements OnInit, OnDestroy {

    model: LocationDescription;
    oldModel: LocationDescription;

    locationDescriptionForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectLocationDescriptionsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectLocationDescriptionById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initLocationDescription();
                    }
                });
            } else {
                this.model = new LocationDescription();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initLocationDescription();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {
                title: this.translate.instant('LOCATION_DESCRIPTION.LIST.TITLE'),
                page: `../default/admin-management/linked-tables/location-descriptions`
            },
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/location-descriptions/edit/${id}`
                    : `../default/admin-management/linked-tables/location-descriptions/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
        this.subscriptions.forEach(el => el.unsubscribe());
    }

    initLocationDescription() {
        this.createForm();
    }


    createForm() {
        this.locationDescriptionForm = this.fb.group({
            sub_community: [this.model.sub_community, Validators.required],
            community: [this.model.community, Validators.required],
            location_description: [this.model.location_description],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('LOCATION_DESCRIPTION.FORM.TITLE.EDIT', {name: this.model.sub_community});
        }
        return this.translate.instant('LOCATION_DESCRIPTION.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.locationDescriptionForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.locationDescriptionForm.markAsPristine();
        this.locationDescriptionForm.markAsUntouched();
        this.locationDescriptionForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.locationDescriptionForm.controls;
        /** check form */
        if (this.locationDescriptionForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateLocationDescription(this.model);
        } else {
            this.createLocationDescription(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: LocationDescription
     */
    updateLocationDescription(_model: LocationDescription) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateLocationDescription: Update<LocationDescription> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new LocationDescriptionUpdated({
            partialLocationDescription: updateLocationDescription,
            locationDescription: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: LocationDescription
     */
    createLocationDescription(_model: LocationDescription) {
        this.store.dispatch(new LocationDescriptionOnServerCreated({locationDescription: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedLocationDescriptionId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model
            && this.model.sub_community && this.model.sub_community.length > 0
            && this.model.community && this.model.community.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }



    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.sub_community != this.oldModel.sub_community
                || this.model.community != this.oldModel.community
                || this.model.location_description != this.oldModel.location_description;
        }
        return false;
    }
}
